import "../App/UserMenu.scss";

import classNames from "classnames";
import React, { ReactNode, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";

import ObjectTools from "/react/components/App/ObjectTools";

import type { MenuItemInterface } from "/react/types";

type Props = {
  menu: {
    items: MenuItemInterface[];
    name?: string;
  };
  reactRouter: boolean;
  currentPath: string;
  // rendered before the menu for including Dashboard, etc.
  children?: ReactNode;
};

/**
 * UserMenu sidebar
 *
 * This may use react-router or not, depending on the reactRouter prop.
 */
const UserMenu = ({
  menu: { items, name },
  reactRouter = false,
  children,
}: Props) => {
  const [activeSection, setActiveSection] = React.useState<string>(null);

  // this should be passed in by reactRouter
  const currentPath = window.location.pathname;

  const urlToSlugMap = useMemo(() => {
    return new Map(items.map((item) => [item.url, item.slug]));
  }, [items]);

  useEffect(() => {
    const ac = urlToSlugMap.get(currentPath);
    // const ac = findActiveSection(items);
    if (ac) {
      setActiveSection(ac);
    } else {
      // if there is only one active/item section (ie: for the client's homepage), uncollapse
      if (items.length == 1 && items[0].items && items[0].items.length > 0) {
        setActiveSection(items[0].slug);
      }
    }
  }, [items, currentPath]);

  if (!items?.length) {
    return null;
  }

  function openSection(item: MenuItemInterface) {
    setActiveSection(activeSection === item.slug ? null : item.slug);
  }

  // Display your name in the menu
  items[0].title = name ?? "My Account";

  const objectTools = (
    <li className="navheader userMenu__item" key="object-tools">
      <ObjectTools />
    </li>
  );

  const listItems = [objectTools].concat(
    items.map((item) => (
      <UserMenuItem
        key={item.slug ?? item.url}
        slug={item.slug}
        icon={item.icon}
        klass={item.klass}
        title={item.title}
        url={item.url}
        items={item.items}
        currentPath={currentPath}
        isOpen={activeSection === item.slug}
        open={openSection}
        reactRouter={reactRouter}
      />
    )),
  );

  return (
    <div className="w-full select-none noprint" role="navigation">
      {children}
      <ul className="w-full">{listItems}</ul>
    </div>
  );
};

export default UserMenu;

type UserMenuItemProps = {
  currentPath: string;
  isOpen: boolean;
  open: (props: MenuItemInterface) => void;
  reactRouter: boolean;
} & MenuItemInterface;

function UserMenuItem(props: UserMenuItemProps) {
  const subItems = props.isOpen ? (
    <ul id={`sub_${props.slug}`} className="userMenu__sub">
      {(props.items ?? []).map((it) => (
        <UserMenuSubItem
          {...it}
          key={it.url ?? it.title}
          currentPath={props.currentPath}
          reactRouter={props.reactRouter}
        />
      ))}
    </ul>
  ) : null;

  const faIcon = `fa fa-${props.icon || "blank"}`;
  const liClass = classNames("userMenu__item", {
    active: props.isOpen,
  });

  let anchor: ReactNode;
  if (props.items) {
    // has sub items
    anchor = (
      <a
        className="collapser"
        onClick={() => {
          props.open(props);
        }}
      >
        <i className={faIcon} /> <span>{props.title}</span>
      </a>
    );
  } else if (props.url.startsWith("/a/")) {
    // internal link to react admin pages
    anchor = (
      <AutoLink url={props.url} reactRouter={props.reactRouter}>
        <i className={faIcon} />{" "}
        <span className="text-nowrap">{props.title}</span>
      </AutoLink>
    );
  } else {
    // standard link
    anchor = (
      <a className="collapser" href={props.url}>
        <i className={faIcon} />{" "}
        <span className="text-nowrap">{props.title}</span>
      </a>
    );
  }

  return (
    <li className={liClass}>
      {anchor}
      {subItems}
    </li>
  );
}

type UserMenuSubItemProps = {
  currentPath: string;
  reactRouter: boolean;
} & MenuItemInterface;

function UserMenuSubItem(props: UserMenuSubItemProps) {
  return props.items ? (
    <li>
      <a>
        <i className={`fa fa-${props.icon || "blank"}`} /> {props.title}
      </a>
      <ul id={`sub_${props.title}`} className="userMenu__sub_item">
        {props.items.map((it) => (
          <UserMenuSubItem
            {...it}
            key={it.url ?? it.title}
            currentPath={props.currentPath}
            reactRouter={props.reactRouter}
          />
        ))}
      </ul>
    </li>
  ) : (
    <li
      className={classNames({ active: props.url === props.currentPath })}
      key={props.url ?? props.title}
    >
      <AutoLink
        url={props.url}
        className={props.klass}
        reactRouter={props.reactRouter}
      >
        <i className={`fa fa-${props.icon || "blank"}`} /> {props.title}
      </AutoLink>
    </li>
  );
}

/**
 * Conditionally renders a link using react-router <NavLink/> or a regular <a> tag
 */
function AutoLink({ reactRouter, url, children, className = "" }) {
  const external = url.startsWith("http");

  if (external || !reactRouter) {
    return (
      <a href={url} className={className}>
        {children}
      </a>
    );
  }

  return (
    <NavLink className={className} to={url}>
      {children}
    </NavLink>
  );
}
